// Dossiers de manquants (`/shortages`) sur l'appareil: lecture de la liste et du
// détail d'un voyage, et saisie d'un constat ou d'un événement sans réseau.
// Ports de backend/app/core/shortages.py (case_measurement_updates,
// case_event_updates) et de ShortageCaseService (liste, contexte du voyage),
// vérifiés par les jeux communs offline_parity/shortage_case.json.
import { lireEntite, lireEntites, ecrireEntiteLocale, lireLecture, marquerDerive } from '../store';
import { nouvelIdentifiant } from '../paths';
import { RefusLocal } from '../droits';
import { metriquesDeManquant } from './manquants';

export const CAS_POSSIBLES = new Set([
  'WITHIN_TOLERANCE', 'TO_REVIEW', 'CONTESTED', 'COMPANY_ACCEPTED', 'ASSIGNED', 'SHARED', 'RECOVERY_IN_PROGRESS', 'RESOLVED',
]);
export const TOLERANCE_PAR_DEFAUT = { liters: 100, percent: 0, rule_id: 'default-voyage-100L' };
export const PARTIES = new Set(['COMPANY', 'DRIVER', 'SUPPLIER', 'CUSTOMER']);

const num = (valeur) => {
  const n = Number(valeur);
  return Number.isFinite(n) ? n : 0;
};
const manquant = (valeur) => valeur === null || valeur === undefined;

const INCONNU = { classification: 'UNKNOWN', shortage_liters: null, surplus_liters: null, delta_liters: null };

/** Port de `case_measurement_updates`. */
export function miseAJourDuConstat(existant, donnees, trajet, commande, tolerance, { organisation, acteur, maintenant }) {
  const cas = existant || {};
  const revision = Number.parseInt(cas.measurement_revision || 0, 10);
  if (cas.measurement_final && !donnees.comment) throw new RefusLocal('Motif de correction requis');
  const valeurs = {};
  ['loaded_volume', 'delivered_volume', 'returned_volume', 'remaining_in_truck'].forEach((cle) => {
    valeurs[cle] = cle in donnees ? donnees[cle] : cas[cle] ?? null;
  });
  Object.entries(valeurs).forEach(([cle, valeur]) => {
    if (!manquant(valeur) && num(valeur) < 0) throw new RefusLocal('Volume négatif');
    void cle;
  });
  const final = Boolean('measurement_final' in donnees ? donnees.measurement_final : cas.measurement_final || false);
  let metriques = metriquesDeManquant({
    loaded: valeurs.loaded_volume,
    delivered: valeurs.delivered_volume,
    returned: valeurs.returned_volume,
    remaining: valeurs.remaining_in_truck,
    final,
    tolerance: tolerance.liters,
    tolerance_percent: tolerance.percent ?? 0,
  });
  if (final && Object.values(valeurs).some(manquant)) metriques = INCONNU;
  const delta = metriques.delta_liters;
  const misAJour = {
    ...valeurs,
    customer_id: commande?.client_id ?? null,
    operation_type: 'TRANSPORT',
    organization_id: organisation,
    reference_type: 'trip',
    reference_id: trajet.id,
    driver_id: trajet.driver_id ?? null,
    truck_id: trajet.truck_id ?? null,
    corridor_id: trajet.corridor_id ?? null,
    measurement_final: final,
    measurement_revision: revision + 1,
    tolerance_snapshot: { ...tolerance },
    measurement_metadata: {
      source: donnees.source || 'web',
      actor_id: acteur,
      document_ids: donnees.document_ids || [],
      captured_at: donnees.captured_at || maintenant,
    },
    shortage_liters: metriques.shortage_liters,
    surplus_liters: metriques.surplus_liters,
    case_status: metriques.classification === 'WITHIN_TOLERANCE' ? 'WITHIN_TOLERANCE' : 'TO_REVIEW',
    status: manquant(delta) ? 'incomplete' : metriques.classification === 'WITHIN_TOLERANCE' ? 'ok' : 'warning',
    requires_approval: false,
    primary_variance_quantity: delta || 0,
    primary_variance_percent: !manquant(delta) && valeurs.loaded_volume ? (num(delta) / num(valeurs.loaded_volume)) * 100 : 0,
    tolerance_quantity: tolerance.liters,
    tolerance_percent: tolerance.percent,
    closed_at: final ? maintenant : null,
  };
  if (existant) misAJour.financial_decision = null;
  return misAJour;
}

/** Port de `case_event_updates` (explication, contestation, décision). */
export function miseAJourDeLEvenement(existant, typeEvenement, donnees, { chauffeur }) {
  if (!existant) throw new RefusLocal('Renseignez d’abord le constat du voyage');
  const revision = Number.parseInt(existant.measurement_revision || 0, 10);
  const misAJour = { measurement_revision: revision + 1 };
  if (typeEvenement === 'CONTEST') {
    misAJour.case_status = 'CONTESTED';
  } else if (typeEvenement === 'DECISION') {
    if (chauffeur) throw new RefusLocal('Décision réservée à la gestion', 403, 'AUTHORIZATION_ERROR');
    if (!CAS_POSSIBLES.has(donnees.case_status)) throw new RefusLocal('Traitement inconnu');
    if (!donnees.comment) throw new RefusLocal('Motif de décision requis');
    const parts = donnees.responsibilities || [];
    const total = parts.reduce((s, p) => s + num(p.amount), 0);
    parts.forEach((p) => {
      if (!PARTIES.has(p.party_type) || num(p.amount) < 0) throw new RefusLocal('Part de responsabilité invalide');
    });
    misAJour.case_status = donnees.case_status;
    misAJour.financial_decision = {
      responsibilities: parts,
      total_amount: String(total),
      currency: donnees.currency || 'XOF',
      comment: donnees.comment,
      revision: revision + 1,
    };
  } else if (typeEvenement !== 'EXPLANATION') {
    throw new RefusLocal('Événement inconnu');
  }
  return misAJour;
}

// --- Lecture ------------------------------------------------------------------------------------

const IDENTITE_VIDE = {};

/** Port de `attach_trip_context`: écarte les dossiers sans voyage et les décrit par leur voyage. */
export async function decrireDossiers(lignes) {
  const [trajets, commandes, camions, convois, chauffeurs, clients] = await Promise.all([
    lireEntites('/trips'), lireEntites('/orders'), lireEntites('/trucks'), lireEntites('/operations'),
    lireEntites('/drivers'), lireEntites('/clients'),
  ]);
  const index = (liste) => Object.fromEntries(liste.map((e) => [String(e.id), e]));
  const parTrajet = index(trajets.filter((t) => !t.deleted));
  const parCommande = index(commandes);
  const parCamion = index(camions);
  const parConvoi = index(convois);
  const parChauffeur = index(chauffeurs);
  const parClient = index(clients);
  return lignes
    .filter((ligne) => ligne.reference_type !== 'trip' || parTrajet[String(ligne.reference_id)])
    .map((ligne) => {
      const trajet = ligne.reference_type === 'trip' ? parTrajet[String(ligne.reference_id)] : null;
      const t = trajet || IDENTITE_VIDE;
      const camion = parCamion[String(t.truck_id)] || IDENTITE_VIDE;
      const commande = parCommande[String(t.order_id)] || IDENTITE_VIDE;
      const convoi = parConvoi[String(t.operation_id)] || IDENTITE_VIDE;
      const chauffeur = parChauffeur[String(t.driver_id || ligne.driver_id)] || IDENTITE_VIDE;
      const client = parClient[String(commande.client_id || ligne.customer_id)] || IDENTITE_VIDE;
      const tracteur = t.tractor_plate || camion.matricule || null;
      return {
        ...ligne,
        convoy_code: convoi.code ?? null,
        order_reference: commande.reference_number ?? null,
        trip_date: t.departure_date || t.created_at || null,
        tractor_plate: tracteur,
        tank_plate: t.tank_plate || camion.tank_number || null,
        truck_matricule: tracteur,
        driver_name: chauffeur.full_name ?? null,
        client_name: client.name ?? null,
      };
    });
}

const FILTRES = { driver_id: 'driver_id', truck_id: 'truck_id', corridor_id: 'corridor_id', case_status: 'case_status', customer_id: 'customer_id', trip_id: 'reference_id' };

const parId = (a, b) => (String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0);

// Champs que la recherche lit, comme le serveur (`row_matches_query`).
const CHAMPS_RECHERCHE = ['convoy_code', 'order_reference', 'tractor_plate', 'tank_plate', 'truck_matricule', 'driver_name', 'client_name', 'case_status', 'status'];
const compacte = (valeur) => String(valeur ?? '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** Une plaque se trouve avec ou sans espaces: « ab123cd » trouve « AB 123 CD ». */
export function correspondALaRecherche(ligne, requete) {
  const aiguille = String(requete || '').toLowerCase().split(/\s+/).filter(Boolean).join(' ');
  if (!aiguille) return true;
  const serree = compacte(aiguille);
  return CHAMPS_RECHERCHE.some((champ) => {
    const valeur = String(ligne[champ] ?? '').toLowerCase();
    return valeur.includes(aiguille) || (serree && compacte(valeur).includes(serree));
  });
}

const plusRecentDAbord = (a, b) => {
  const da = String(a.created_at || '');
  const db = String(b.created_at || '');
  if (da !== db) return da < db ? 1 : -1;
  return parId(b, a);
};

/**
 * `GET /shortages`: dossiers (avec un constat), filtrés, décrits par leur voyage.
 * Avec `page`, une tranche du plus récent au plus ancien et le `meta` du serveur
 * (total, manquant constaté de tous les dossiers retenus).
 */
export async function listerDossiers(parametres = {}) {
  let lignes = (await lireEntites('/shortages')).filter((l) => l.reference_type === 'trip' && l.case_status);
  Object.entries(FILTRES).forEach(([param, champ]) => {
    if (parametres[param]) lignes = lignes.filter((l) => String(l[champ]) === String(parametres[param]));
  });
  let decrits = await decrireDossiers(lignes.sort(parId));
  if (String(parametres.q || '').trim()) decrits = decrits.filter((l) => correspondALaRecherche(l, parametres.q));
  const page = Number.parseInt(parametres.page, 10);
  if (!Number.isFinite(page) || page < 1) return { success: true, data: decrits };
  const taille = Math.min(Math.max(Number.parseInt(parametres.page_size, 10) || 50, 1), 500);
  const recents = [...decrits].sort(plusRecentDAbord);
  return {
    success: true,
    data: recents.slice((page - 1) * taille, page * taille),
    meta: {
      page,
      page_size: taille,
      total: recents.length,
      total_shortage_liters: recents.reduce((somme, l) => somme + Number(l.shortage_liters || 0), 0),
    },
  };
}

const evenementsDuVoyage = async (tripId) => {
  const locaux = await lireEntites(`/shortages/trips/${tripId}/events`);
  return locaux;
};

/** `GET /shortages/trips/:id`: le dossier, son historique et ses chiffres. */
export async function detailDuDossier(tripId) {
  const trajet = await lireEntite('/trips', tripId);
  const lignes = (await lireEntites('/shortages')).filter((l) => l.reference_type === 'trip' && String(l.reference_id) === String(tripId));
  const dossier = lignes[0] || null;
  if (!trajet && !dossier) return undefined;
  // L'historique déjà reçu du serveur, complété par les événements saisis ici.
  const instantane = await lireLecture(`/shortages/trips/${tripId}`);
  const recus = instantane?.data?.data?.events || instantane?.data?.events || [];
  const locaux = await evenementsDuVoyage(tripId);
  const vus = new Set(recus.map((e) => String(e.id)));
  const evenements = [...recus, ...locaux.filter((e) => !vus.has(String(e.id)))].sort(
    (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.id).localeCompare(String(b.id)),
  );
  let metriques = null;
  if (dossier) {
    const tolerance = dossier.tolerance_snapshot || {};
    metriques = metriquesDeManquant({
      loaded: dossier.loaded_volume,
      delivered: dossier.delivered_volume,
      returned: dossier.returned_volume,
      remaining: dossier.remaining_in_truck,
      final: dossier.measurement_final,
      tolerance: tolerance.liters ?? 100,
      tolerance_percent: tolerance.percent ?? 0,
    });
    if (dossier.measurement_final && ['loaded_volume', 'delivered_volume', 'returned_volume', 'remaining_in_truck'].some((cle) => manquant(dossier[cle]))) {
      metriques = INCONNU;
    }
  }
  const base = instantane?.data?.data?.trip || {};
  return {
    success: true,
    data: {
      trip: {
        id: tripId,
        driver_id: trajet?.driver_id ?? base.driver_id ?? null,
        truck_id: trajet?.truck_id ?? base.truck_id ?? null,
        corridor_id: trajet?.corridor_id ?? base.corridor_id ?? null,
      },
      case: dossier,
      events: evenements,
      metrics: metriques,
    },
  };
}

// --- Écriture: `POST /shortages/trips/:id/events` --------------------------------------------------

const GESTION = new Set(['SUPER_ADMIN', 'ADMIN_SOCIETE', 'LOGISTIQUE', 'GESTIONNAIRE_STATION', 'CONTROLEUR_CARBURANT', 'ADMIN']);

export async function appliquerEvenementManquant(ctx, { role, utilisateur }) {
  const tripId = ctx.match[1];
  const donnees = ctx.corps || {};
  const chauffeur = role === 'CHAUFFEUR';
  if (!chauffeur && role && !GESTION.has(role)) throw new RefusLocal('Action réservée à la gestion', 403, 'AUTHORIZATION_ERROR');
  const typeEvenement = donnees.event_type || 'MEASUREMENT';
  if (chauffeur && !['EXPLANATION', 'CONTEST'].includes(typeEvenement)) {
    throw new RefusLocal('Le chauffeur peut répondre ou contester, sans attribuer de responsabilité', 403, 'AUTHORIZATION_ERROR');
  }
  const trajet = await lireEntite('/trips', tripId);
  if (!trajet) return null;
  const lignes = (await lireEntites('/shortages')).filter((l) => l.reference_type === 'trip' && String(l.reference_id) === String(tripId));
  const existant = lignes[0] || null;
  const revision = Number.parseInt(existant?.measurement_revision || 0, 10);
  if (['MEASUREMENT', 'DECISION'].includes(typeEvenement) && donnees.expected_revision !== undefined && donnees.expected_revision !== null && donnees.expected_revision !== revision) {
    throw new RefusLocal('Le dossier a changé ; rechargez avant de confirmer');
  }
  const maintenant = new Date().toISOString();
  const acteur = utilisateur?.user_id || utilisateur?.id || 'SYSTEM';
  let misAJour;
  if (typeEvenement === 'MEASUREMENT') {
    const commande = trajet.order_id ? await lireEntite('/orders', trajet.order_id) : null;
    const tolerance = existant?.tolerance_snapshot || TOLERANCE_PAR_DEFAUT;
    misAJour = miseAJourDuConstat(existant, donnees, trajet, commande, tolerance, {
      organisation: trajet.organization_id ?? null,
      acteur,
      maintenant,
    });
  } else {
    misAJour = miseAJourDeLEvenement(existant, typeEvenement, donnees, { chauffeur });
  }
  const identifiant = existant?.id || nouvelIdentifiant();
  const dossier = { ...(existant || {}), ...misAJour, id: identifiant, updated_at: maintenant, ...(existant ? {} : { created_at: maintenant }) };
  await ecrireEntiteLocale('/shortages', identifiant, dossier);
  const evenement = {
    id: nouvelIdentifiant(),
    organization_id: dossier.organization_id ?? null,
    reconciliation_id: identifiant,
    actor_id: acteur,
    event_type: typeEvenement,
    revision: revision + 1,
    details: { before: existant, after: dossier, input: donnees },
    created_at: maintenant,
    local: true,
  };
  await ecrireEntiteLocale(`/shortages/trips/${tripId}/events`, evenement.id, evenement);
  // Un dossier créé ici n'a pas l'identifiant du serveur: sa ligne n'est qu'un calcul
  // local, remplacé par la vraie à la prochaine mise à jour. Un dossier existant
  // est modifié en place.
  const touches = [{ kind: `/shortages/trips/${tripId}/events`, id: evenement.id, derive: true }];
  await marquerDerive(`/shortages/trips/${tripId}/events`, evenement.id);
  if (existant) touches.unshift({ kind: '/shortages', id: identifiant });
  else {
    await marquerDerive('/shortages', identifiant);
    touches.unshift({ kind: '/shortages', id: identifiant, derive: true });
  }
  return { reponse: await detailDuDossier(tripId), statut: 200, touches };
}
